import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { ApiError } from '../api';

/** 비동기 데이터 로딩. reload() 로 다시 불러온다. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    fn().then((d) => { if (alive) setData(d); }).catch((e) => { if (alive) setError(errorText(e)); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { data, error, loading, reload: () => setTick((t) => t + 1) };
}

/** 오류 문구. 입력값 오류는 서버가 알려 준 첫 칸의 이유를 보여 준다("입력값을 확인하세요"만으로는 무엇이 틀렸는지 모름). */
export const errorText = (e: unknown) => {
  if (e instanceof ApiError && e.code === 'VALIDATION' && e.fields?.[0]?.message) return e.fields[0].message;
  return e instanceof ApiError || e instanceof Error ? e.message : '알 수 없는 오류가 발생했습니다.';
};

export function PageHeader({ title, sub, actions, eyebrow }: { title: string; sub?: string; actions?: ReactNode; eyebrow?: string }) {
  return (
    <div className="page-head">
      <div>{eyebrow && <p className="page-eyebrow">{eyebrow}</p>}<h1>{title}</h1>{sub && <p className="muted">{sub}</p>}</div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

export function Alert({ kind = 'error', children }: { kind?: 'error' | 'info' | 'ok'; children: ReactNode }) {
  return <div className={`alert alert-${kind}`} role={kind === 'error' ? 'alert' : 'status'}>{children}</div>;
}

export function Loading({ what = '불러오는 중' }: { what?: string }) {
  return <p className="muted loading" aria-live="polite">{what}…</p>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Chip({ tone, children }: { tone: 'ok' | 'warn' | 'danger' | 'info' | 'muted'; children: ReactNode }) {
  return <span className={`chip chip-${tone}`}>{children}</span>;
}

export function MonthPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const shift = (d: number) => {
    const [y, m] = value.split('-').map(Number) as [number, number];
    const dt = new Date(Date.UTC(y, m - 1 + d, 1));
    onChange(dt.toISOString().slice(0, 7));
  };
  return (
    <div className="month-picker" role="group" aria-label="조회 월">
      <button type="button" className="btn btn-ghost" onClick={() => shift(-1)} aria-label="이전 달">‹</button>
      <input id="month" type="month" value={value} onChange={(e) => e.target.value && onChange(e.target.value)} aria-label="조회 월 선택" />
      <button type="button" className="btn btn-ghost" onClick={() => shift(1)} aria-label="다음 달">›</button>
    </div>
  );
}

/**
 * 모달 대화상자(<dialog>). 열릴 때 첫 입력에 포커스, Esc 로 닫힘, 닫히면 원래 버튼으로 포커스 복귀.
 * onSubmit 이 오류를 던지면 대화상자 안에 메시지를 보여준다.
 */
/**
 * ESC 로 대화상자 닫기. 브라우저 기본 동작도 있지만, 한글을 입력하는 중(조합 중)에는 첫 ESC 를 입력기가 가져가
 * 대화상자가 안 닫혀 보였다. 키의 위치(code)로 보고 바로 닫는다. 안쪽 목록(검색 선택 등)이 먼저 처리한 ESC 는 건드리지 않는다.
 */
function escClose(e: KeyboardEvent<HTMLDialogElement>, onClose: (() => void) | undefined) {
  if (e.defaultPrevented || (e.key !== 'Escape' && e.code !== 'Escape')) return;
  e.preventDefault();
  onClose?.();
}

export function Dialog({ title, open, onClose, onSubmit, submitLabel, danger, wide, children, keepOpen, hideSubmit }: {
  title: string; open: boolean; onClose: () => void; onSubmit: () => Promise<void>; submitLabel: string; danger?: boolean; wide?: boolean; children: ReactNode;
  /** 제출 후에도 닫지 않는다(결제창처럼 결과가 나중에 오는 경우, 닫기는 onClose 를 직접) */
  keepOpen?: boolean;
  hideSubmit?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) { setError(null); d.showModal(); d.querySelector<HTMLElement>('input,select,textarea')?.focus(); }
    if (!open && d.open) d.close();
  }, [open]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await onSubmit(); if (!keepOpen) onClose(); } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  };
  return (
    <dialog ref={ref} className={`dialog${wide ? ' dialog-wide' : ''}`} onClose={onClose} onKeyDown={(e) => escClose(e, busy ? undefined : onClose)} aria-labelledby={titleId}>
      <form onSubmit={submit} className="dialog-body">
        <h2 id={titleId}>{title}</h2>
        {children}
        {error && <Alert>{error}</Alert>}
        <div className="dialog-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>취소</button>
          {!hideSubmit && <button type="submit" className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} disabled={busy}>{busy ? '처리 중…' : submitLabel}</button>}
        </div>
      </form>
    </dialog>
  );
}

export function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && <p className="hint" id={`${id}-hint`}>{hint}</p>}
    </div>
  );
}

/** 보기 전용 대화상자(<dialog>): 닫기 버튼 + 선택 동작. Esc 로 닫히고 원래 버튼으로 포커스가 돌아간다. */
export function ViewDialog({ title, open, onClose, wide, actions, children, className }: {
  title: string; open: boolean; onClose: () => void; wide?: boolean; actions?: ReactNode; children: ReactNode; className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className={`dialog${wide ? ' dialog-wide' : ''}${className ? ` ${className}` : ''}`} onClose={onClose} onKeyDown={(e) => escClose(e, onClose)} aria-labelledby={id}>
      <div className="dialog-body">
        <div className="view-dialog-head">
          <h2 id={id}>{title}</h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>닫기</button>
        </div>
        {open && children}
        {actions && <div className="dialog-actions">{actions}</div>}
      </div>
    </dialog>
  );
}
